import { useEffect, useState } from 'react';
import type { ConsentCallbackMessage } from '../lib/consent';
import type { IntegrationName } from '../lib/api';

/**
 * Redirect target (redirect_uri) for the Microsoft admin-consent popup flow.
 *
 * Microsoft returns one of:
 *   - admin_consent=True&tenant=<id>&state=<our state>   (success)
 *   - error=...&error_description=...&state=<our state>  (failure / declined)
 *
 * This page parses those params, posts the outcome to the opener window (the Integrations
 * page), then closes itself. It performs no authenticated work — the parent window records
 * the audit outcome once it receives the message.
 */
export default function AdminConsentCallback() {
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    let integration: IntegrationName = 'onboarding';
    let correlationId: string | undefined;
    const stateRaw = params.get('state');
    if (stateRaw) {
      try {
        const parsed = JSON.parse(stateRaw) as { integration?: IntegrationName; correlationId?: string };
        if (parsed.integration) integration = parsed.integration;
        correlationId = parsed.correlationId;
      } catch {
        // Ignore malformed state; fall back to defaults.
      }
    }

    const adminConsent = params.get('admin_consent');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    const tenant = params.get('tenant') ?? undefined;

    const success = adminConsent?.toLowerCase() === 'true' && !error;

    const message: ConsentCallbackMessage = {
      source: 'quickdial-admin-consent',
      integration,
      status: success ? 'success' : 'failed',
      tenant,
      error: success ? undefined : (errorDescription || error || 'Admin consent was not granted.'),
      correlationId,
    };

    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(message, window.location.origin);
      } catch {
        // Ignore cross-origin posting errors; the opener also polls for popup close.
      }
      window.close();
      // If the browser refuses to close the window programmatically, show a fallback message.
      setTimeout(() => setClosed(true), 400);
    } else {
      setClosed(true);
    }
  }, []);

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', padding: '2rem', textAlign: 'center' }}>
      <h2>Admin consent {closed ? 'complete' : 'processing…'}</h2>
      <p>
        {closed
          ? 'You can close this window and return to the Integrations page.'
          : 'Finishing up, this window will close automatically.'}
      </p>
    </div>
  );
}
